import type { FilterConfig } from '@/types/filters'
import { LOTE_1_FILTER_CONFIGS } from './filterConfigs'

/**
 * LOTE 2: Configurações de filtros para dados pessoais e contratuais
 * 
 * DADOS PESSOAIS (7 campos):
 * - Data de Nascimento
 * - CPF
 * - Sexo
 * - Estado Civil
 * - Nacionalidade  
 * - Raça
 * - Naturalidade
 * 
 * DADOS CONTRATUAIS (8 campos):
 * - Tipo de Colaborador (CLT/PJ)
 * - Data de Admissão
 * - Data de Demissão
 * - Jornada de Trabalho
 * - Senioridade
 * - Rating
 * - Tipo de Papel
 * - Código eSocial
 */

export const LOTE_2_FILTER_CONFIGS: FilterConfig[] = [
  // ===== DADOS PESSOAIS =====
  {
    columnId: 'birthDate',
    label: 'Data de Nascimento',
    type: 'date',
    operators: ['equals', 'greaterThan', 'lessThan', 'between'],
    placeholder: 'dd/mm/aaaa',
    category: 'Dados Pessoais'
  },
  {
    columnId: 'cpf',
    label: 'CPF',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith'],
    placeholder: 'Digite o CPF...',
    category: 'Dados Pessoais'
  },
  {
    columnId: 'sex',
    label: 'Sexo',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'M', label: 'Masculino' },
      { value: 'F', label: 'Feminino' },
      { value: 'O', label: 'Outro' },
      { value: 'N', label: 'Não Informado' }
    ],
    placeholder: 'Selecione o sexo...',
    category: 'Dados Pessoais'
  },
  {
    columnId: 'maritalStatus',
    label: 'Estado Civil',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'single', label: 'Solteiro(a)' },
      { value: 'married', label: 'Casado(a)' },
      { value: 'divorced', label: 'Divorciado(a)' },
      { value: 'widowed', label: 'Viúvo(a)' },
      { value: 'stable_union', label: 'União Estável' },
      { value: 'separated', label: 'Separado(a)' }
    ],
    placeholder: 'Selecione o estado civil...',
    category: 'Dados Pessoais'
  },
  {
    columnId: 'nationality',
    label: 'Nacionalidade',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'contains'],
    options: [], // Será populado dinamicamente
    placeholder: 'Selecione a nacionalidade...',
    category: 'Dados Pessoais'
  },
  {
    columnId: 'race',
    label: 'Raça/Cor',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'white', label: 'Branca' },
      { value: 'black', label: 'Preta' },
      { value: 'brown', label: 'Parda' },
      { value: 'yellow', label: 'Amarela' },
      { value: 'indigenous', label: 'Indígena' },
      { value: 'not_informed', label: 'Não Informado' }
    ],
    placeholder: 'Selecione a raça/cor...',
    category: 'Dados Pessoais'
  },
  {
    columnId: 'naturality',
    label: 'Naturalidade',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith'],
    placeholder: 'Digite a naturalidade...',
    category: 'Dados Pessoais'
  },
  
  // ===== DADOS CONTRATUAIS =====
  {
    columnId: 'collaboratorType',
    label: 'Tipo de Colaborador',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'CLT', label: 'CLT' },
      { value: 'PJ', label: 'Pessoa Jurídica' },
      { value: 'INTERN', label: 'Estagiário' },
      { value: 'APPRENTICE', label: 'Aprendiz' },
      { value: 'FREELANCER', label: 'Freelancer' },
      { value: 'CONSULTANT', label: 'Consultor' }
    ],
    placeholder: 'Selecione o tipo...',
    category: 'Dados Contratuais'
  },
  {
    columnId: 'startDate',
    label: 'Data de Admissão',
    type: 'date',
    operators: ['equals', 'greaterThan', 'lessThan', 'between'],
    placeholder: 'dd/mm/aaaa',
    category: 'Dados Contratuais'
  },
  {
    columnId: 'endDate',
    label: 'Data de Demissão',
    type: 'date',
    operators: ['equals', 'greaterThan', 'lessThan', 'between', 'isEmpty', 'isNotEmpty'],
    placeholder: 'dd/mm/aaaa',
    category: 'Dados Contratuais'
  },
  {
    columnId: 'workSchedule',
    label: 'Jornada de Trabalho',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'contains'],
    options: [], // Será populado dinamicamente
    placeholder: 'Selecione a jornada...',
    category: 'Dados Contratuais'
  },
  {
    columnId: 'seniorityName',
    label: 'Nível de Senioridade',
    type: 'multiselect',
    operators: ['in', 'notIn'],
    options: [], // Será populado dinamicamente
    placeholder: 'Selecione uma ou mais senioridades...',
    category: 'Dados Contratuais'
  },
  {
    columnId: 'rating',
    label: 'Rating',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'greaterThan', 'lessThan'],
    options: [
      { value: 'A', label: 'A - Excelente' },
      { value: 'B', label: 'B - Muito Bom' },
      { value: 'C', label: 'C - Bom' },
      { value: 'D', label: 'D - Regular' },
      { value: 'E', label: 'E - Insatisfatório' },
      { value: 'NOT_RATED', label: 'Não Avaliado' }
    ],
    placeholder: 'Selecione o rating...',
    category: 'Dados Contratuais'
  },
  {
    columnId: 'roleType',
    label: 'Tipo de Papel',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'IC', label: 'Individual Contributor' },
      { value: 'TL', label: 'Tech Lead' },
      { value: 'SM', label: 'Scrum Master' },
      { value: 'PO', label: 'Product Owner' },
      { value: 'PM', label: 'Project Manager' },
      { value: 'MANAGER', label: 'Manager' },
      { value: 'DIRECTOR', label: 'Director' },
      { value: 'OTHER', label: 'Outros' }
    ],
    placeholder: 'Selecione o tipo de papel...',
    category: 'Dados Contratuais'
  },
  {
    columnId: 'esocialCode',
    label: 'Código eSocial',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith'],
    placeholder: 'Digite o código eSocial...',
    category: 'Dados Contratuais'
  }
]

/**
 * Configurações combinadas: Lote 1 + Lote 2
 */
export { LOTE_1_FILTER_CONFIGS }
export { updateFilterConfigsWithData } from './filterConfigs'

export const LOTES_1_E_2_FILTER_CONFIGS: FilterConfig[] = [
  ...LOTE_1_FILTER_CONFIGS,
  ...LOTE_2_FILTER_CONFIGS
]

/**
 * Acessores para campos do Lote 2
 * Campos que precisam de transformação ou acesso especial
 */
export const LOTE_2_FIELD_ACCESSORS = {
  // Campos que já existem diretamente no objeto
  birthDate: (item: any) => item.birthDate,
  cpf: (item: any) => item.cpf,
  sex: (item: any) => item.sex,
  maritalStatus: (item: any) => item.maritalStatus,
  nationality: (item: any) => item.nationality,
  race: (item: any) => item.race,
  naturality: (item: any) => item.naturality,
  collaboratorType: (item: any) => item.collaboratorType,
  startDate: (item: any) => item.startDate,
  endDate: (item: any) => item.endDate,
  workSchedule: (item: any) => item.workSchedule,
  seniorityName: (item: any) => item.seniorityName,
  rating: (item: any) => item.rating,
  roleType: (item: any) => item.roleType,
  esocialCode: (item: any) => item.esocialCode,
}

/**
 * Presets prontos para Lote 2
 */
export const LOTE_2_PRESETS = [
  {
    id: 'colaboradores_clt_ativos',
    name: 'Colaboradores CLT Ativos',
    description: 'Colaboradores com vínculo CLT e status ativo',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'collaboratorType',
        operator: 'equals' as const,
        value: 'CLT',
        type: 'select' as const,
        label: 'Tipo de Colaborador',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'estagiarios_ativos',
    name: 'Estagiários Ativos',
    description: 'Estagiários com status ativo',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'collaboratorType',
        operator: 'equals' as const,
        value: 'INTERN',
        type: 'select' as const,
        label: 'Tipo de Colaborador',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'colaboradores_rating_a_b',
    name: 'Colaboradores High Performers',
    description: 'Colaboradores com rating A ou B',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'rating',
        operator: 'in' as const,
        value: ['A', 'B'],
        type: 'multiselect' as const,
        label: 'Rating',
        enabled: true
      }
    ],
    isSystem: true
  }
] as const
